// src/components/.../AiFoodCatalog.js

import React, { useState, useCallback, useMemo } from 'react';
import { db } from '../../../../../../firebase/firebaseConfig';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { FcSynchronize } from 'react-icons/fc';
import { buildFoodFilterFromProfile } from '../../../../../../helpers/foodFilterBuilder';
import { applyFoodFilter } from '../../../../../../helpers/foodFilterApplier';
import { getRelevantBadges } from '../../../../../../helpers/foodBadgeHelper';
import './AiFoodCatalog.css';

// Page size for recommendations display
const PAGE_SIZE = 3;

// Food categories based on tags from the RULES array in tag-minimal-fineli-csv.js
const FOOD_CATEGORIES = [
  { label: 'Food Category', value: 'all', tags: [] },
  { label: 'Bread & Bakery', value: 'Bread & Bakery', tags: ['bread_bakery'] },
  { label: 'Grains & Cereals', value: 'grain_cereal', tags: ['grain_cereal'] },
  { label: 'Dairy', value: 'dairy', tags: ['dairy'] },
  { label: 'Meat', value: 'meat', tags: ['meat'] },
  { label: 'Poultry', value: 'poultry', tags: ['poultry'] },
  { label: 'Organ Meats', value: 'organ_meat', tags: ['organ_meat'] },
  { label: 'Fish & Seafood', value: 'fish_seafood', tags: ['fish_seafood'] },
  {
    label: 'Legumes & Alternative Proteins',
    value: 'legume_alt_protein',
    tags: ['legume_alt_protein'],
  },
  { label: 'Eggs', value: 'eggs', tags: ['eggs'] },
  { label: 'Nuts & Seeds', value: 'nuts_seeds', tags: ['nuts_seeds'] },
  { label: 'Vegetables', value: 'vegetable', tags: ['vegetable'] },
  { label: 'Fruits', value: 'fruit', tags: ['fruit'] },
  { label: 'Mushrooms', value: 'mushroom', tags: ['mushroom'] },
  { label: 'Seaweed', value: 'seaweed', tags: ['seaweed'] },
  { label: 'Salads', value: 'salad', tags: ['salad'] },
  {
    label: 'Prepared Dishes & Ready Meals',
    value: 'prepared_dish',
    tags: ['prepared_dish'],
  },
  { label: 'Snacks', value: 'snack', tags: ['snack'] },
  { label: 'Sweets & Desserts', value: 'sweets', tags: ['sweets'] },
  { label: 'Oils & Fats', value: 'oils_fats', tags: ['oils_fats'] },
  {
    label: 'Spices & Seasonings',
    value: 'spices_and_seasonings',
    tags: ['spice', 'condiment', 'salt', 'sweetener'],
  },
  { label: 'Baking Ingredients', value: 'baking_ingredient', tags: ['baking_ingredient'] },
  { label: 'Baby Food & Formula', value: 'babyfood', tags: ['babyfood'] },
  { label: 'Beverages', value: 'beverage', tags: ['beverage'] },
  { label: 'Alcoholic Drinks', value: 'alcohol', tags: ['alcohol'] },
  { label: 'Sports Nutrition', value: 'sports_nutrition', tags: ['sports_nutrition'] },
  { label: 'Other / Unclassified', value: 'other', tags: ['other'] },
];

// Dietary attribute filters
const DIETARY_FILTERS = [
  { label: 'Gluten-Free', value: 'gf', tag: 'gf' },
  { label: 'Plant-Based', value: 'plant_based', tag: 'plant_based' },
  { label: 'Lactose-Free', value: 'lactose_opt', tag: 'lactose_opt' },
  { label: 'Low-Fat', value: 'light', tag: 'light' },
];

/**
 * Navigate to /my-nutrition-status and open the settings sheet via #settings.
 * - If already on /my-nutrition-status, just toggle the hash so the hashchange
 *   listener in MyNutritionStatus can open the settings.
 * - Otherwise, navigate to that route with the hash.
 */
const goToNutritionSettings = () => {
  if (typeof window === 'undefined') return;

  const targetPath = '/my-nutrition-status';
  const targetHash = '#settings';

  if (window.location.pathname === targetPath) {
    // If already on the page: force a real hash change so the listener fires
    if (window.location.hash === targetHash) {
      window.location.hash = '';
    }
    window.location.hash = targetHash;
  } else {
    window.location.assign(`${targetPath}${targetHash}`);
  }
};

// Nutrient mapping for user deficiencies
const NUTRIENT_FIELD_MAP = {
  vitamin_c: 'vitamin_c_mg',
  calcium: 'calcium_mg',
  iron: 'iron_mg',
  magnesium: 'magnesium_mg',
  vitamin_a: 'vitamin_a_RE_µg',
  potassium: 'potassium_mg',
  zinc: 'zinc_mg',
  protein: 'proteinG',
  fat: 'fatG',
};

/**
 * AiFoodCatalog - Food category dropdown component with recommendations
 *
 * @param {Function} onCategoryChange
 * @param {Function} onDietaryFilterChange
 * @param {Array} userDeficiencies - nutrient deficiencies
 * @param {Object} userProfile - Firestore user profile with mealPref_* fields
 */
function AiFoodCatalog({
  onCategoryChange,
  onDietaryFilterChange,
  userDeficiencies = [],
  userProfile,
}) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cache for full filtered lists per category
  const [eligibleFoodsByCategory, setEligibleFoodsByCategory] = useState({});

  // Current page index for pagination
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  // Build food filter from user's meal preferences
  const userFoodFilter = useMemo(() => {
    if (!userProfile) {
      return null;
    }

    const filter = buildFoodFilterFromProfile(userProfile);
    return filter;
  }, [userProfile]);

  /**
   * Fetch food recommendations based on category and user deficiencies
   */
  const fetchRecommendations = useCallback(
    async (category, dietaryFilters = []) => {
      if (category === 'all') {
        setRecommendations([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Try to fetch from snapshot first (optimized path)
        const snapshotRef = doc(db, 'foodCatalogSnapshot', category);
        const snapshotDoc = await getDoc(snapshotRef);

        let foods = [];

        if (snapshotDoc.exists()) {
          const snapshotFoods = snapshotDoc.data().foods || [];
          foods = snapshotFoods.map((food) => ({
            ...food,
            tagsV2: food.tagsV2 || food.tags || [],
          }));
        } else {
          // Fallback: query the main collection
          const foodsRef = collection(db, 'foodCatalog');
          const categoryTag = `cat:${category}`;

          const foodsQuery = query(
            foodsRef,
            where('tagsV2', 'array-contains', categoryTag)
          );

          const querySnapshot = await getDocs(foodsQuery);

          foods = querySnapshot.docs.map((docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              ...data,
              tagsV2: data.tagsV2 || data.tags || [],
            };
          });
        }

        // Apply dietary filters if any
        if (dietaryFilters.length > 0) {
          foods = foods.filter((food) => {
            const foodTags = food.tagsV2 || [];
            return dietaryFilters.every((filterValue) =>
              foodTags.some((tag) => tag.includes(filterValue))
            );
          });
        }

        // Rank foods by nutrient deficiencies
        if (userDeficiencies.length > 0) {
          // Focus on the most critical deficiency
          const primaryDeficiency = userDeficiencies[0];
          const nutrientField =
            NUTRIENT_FIELD_MAP[primaryDeficiency.nutrient];

          if (nutrientField) {
            foods.sort((leftFood, rightFood) => {
              const leftValue =
                leftFood.nutrientsPer100g?.[nutrientField] || 0;
              const rightValue =
                rightFood.nutrientsPer100g?.[nutrientField] || 0;

              return rightValue - leftValue;
            });
          }
        }

        // Apply meal preference filters if user has preferences set
        if (userFoodFilter && userFoodFilter.hasPreferences) {
          foods = applyFoodFilter(foods, userFoodFilter);
        }

        // Cache the full filtered and sorted list for this category
        setEligibleFoodsByCategory((previous) => ({
          ...previous,
          [category]: foods,
        }));

        // Reset to first page
        setCurrentPageIndex(0);

        // Display first page (first PAGE_SIZE items)
        const firstPage = foods.slice(0, PAGE_SIZE);
        setRecommendations(firstPage);
      } catch (fetchError) {
        // Consume error to avoid unused variable warnings without logging
        String(fetchError);
        setError('Failed to load food recommendations');
        setRecommendations([]);
      } finally {
        setLoading(false);
      }
    },
    [userDeficiencies, userFoodFilter]
  );

  /**
   * Handle category change
   */
  const handleCategoryChange = (event) => {
    const newCategory = event.target.value;
    setSelectedCategory(newCategory);

    // Find the selected category object
    const categoryObject = FOOD_CATEGORIES.find(
      (category) => category.value === newCategory
    );

    // Notify parent component
    if (onCategoryChange) {
      onCategoryChange({
        category: newCategory,
        tags: categoryObject?.tags || [],
      });
    }

    // Notify parent about dietary filter changes if needed later
    if (onDietaryFilterChange) {
      onDietaryFilterChange([]);
    }

    // Check if we have cached data for this category
    if (
      newCategory !== 'all' &&
      eligibleFoodsByCategory[newCategory]
    ) {
      // Reset to first page
      setCurrentPageIndex(0);

      // Display first page from cache
      const cachedFoods = eligibleFoodsByCategory[newCategory];
      const firstPage = cachedFoods.slice(0, PAGE_SIZE);

      setRecommendations(firstPage);
    } else {
      // Fetch recommendations for the new category (not cached)
      fetchRecommendations(newCategory, []);
    }
  };

  /**
   * Handle FcSynchronize click - pure in-memory pagination
   */
  const handleRefreshClick = () => {
    if (selectedCategory === 'all' || loading) return;

    // Get the full cached list for this category
    const eligibleFoods = eligibleFoodsByCategory[selectedCategory];

    if (!eligibleFoods || eligibleFoods.length === 0) {
      return;
    }

    // Increment page index
    const nextPageIndex = currentPageIndex + 1;
    const startIndex = nextPageIndex * PAGE_SIZE;

    // Check if we need to wrap around
    let finalPageIndex = nextPageIndex;

    if (startIndex >= eligibleFoods.length) {
      // Wrap to first page
      finalPageIndex = 0;
    }

    setCurrentPageIndex(finalPageIndex);

    // Get the page slice
    const pageStart = finalPageIndex * PAGE_SIZE;
    const pageEnd = pageStart + PAGE_SIZE;
    const nextPage = eligibleFoods.slice(pageStart, pageEnd);

    setRecommendations(nextPage);
  };

  /**
   * Format nutrient value for display
   */
  const formatNutrientValue = (value, unit) => {
    if (!value) return '0';

    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue)) {
      return '0';
    }

    return `${numericValue.toFixed(1)}${unit}`;
  };

  /**
   * Get the primary nutrient to highlight based on user deficiencies
   */
  const getPrimaryNutrient = () => {
    if (userDeficiencies.length === 0) return null;
    return userDeficiencies[0];
  };

  /**
   * Get top N vitamins and minerals for a food item
   * Returns an array of {name, value, unit} objects
   */
  const getTopNutrients = (food, count = 5) => {
    if (!food.nutrientsPer100g) return [];

    // Define all vitamins and minerals we want to check
    const nutrientMap = [
      { field: 'vitamin_c_mg', name: 'Vitamin C', unit: 'mg' },
      { field: 'vitamin_a_RE_µg', name: 'Vitamin A', unit: 'µg' },
      { field: 'vitamin_d_µg', name: 'Vitamin D', unit: 'µg' },
      { field: 'vitamin_e_mg', name: 'Vitamin E', unit: 'mg' },
      { field: 'vitamin_k_µg', name: 'Vitamin K', unit: 'µg' },
      { field: 'thiamine_mg', name: 'B1', unit: 'mg' },
      { field: 'riboflavin_mg', name: 'B2', unit: 'mg' },
      { field: 'niacin_mg', name: 'B3', unit: 'mg' },
      { field: 'vitamin_b6_mg', name: 'B6', unit: 'mg' },
      { field: 'folate_µg', name: 'Folate', unit: 'µg' },
      { field: 'vitamin_b12_µg', name: 'B12', unit: 'µg' },
      { field: 'calcium_mg', name: 'Calcium', unit: 'mg' },
      { field: 'iron_mg', name: 'Iron', unit: 'mg' },
      { field: 'magnesium_mg', name: 'Magnesium', unit: 'mg' },
      { field: 'phosphorus_mg', name: 'Phosphorus', unit: 'mg' },
      { field: 'potassium_mg', name: 'Potassium', unit: 'mg' },
      { field: 'sodium_mg', name: 'Sodium', unit: 'mg' },
      { field: 'zinc_mg', name: 'Zinc', unit: 'mg' },
      { field: 'copper_mg', name: 'Copper', unit: 'mg' },
      { field: 'selenium_µg', name: 'Selenium', unit: 'µg' },
      { field: 'iodine_µg', name: 'Iodine', unit: 'µg' },
    ];

    // Get all nutrients with their values
    const nutrients = nutrientMap
      .map((nutrient) => ({
        name: nutrient.name,
        value: food.nutrientsPer100g[nutrient.field] || 0,
        unit: nutrient.unit,
        field: nutrient.field,
      }))
      // Only include nutrients that have a positive value
      .filter((nutrient) => nutrient.value > 0)
      // Sort them by normalized value (µg → mg for comparison)
      .sort((leftNutrient, rightNutrient) => {
        const leftValueNormalized =
          leftNutrient.unit === 'µg'
            ? leftNutrient.value / 1000
            : leftNutrient.value;
        const rightValueNormalized =
          rightNutrient.unit === 'µg'
            ? rightNutrient.value / 1000
            : rightNutrient.value;

        return rightValueNormalized - leftValueNormalized;
      })
      .slice(0, count);

    return nutrients;
  };

  // Helper to check if a category should be hidden based on user preferences
  const isCategoryForbidden = useCallback(
    (categoryValue) => {
      if (!userFoodFilter || !userFoodFilter.hasPreferences) return false;
      if (!userProfile) return false;
      if (categoryValue === 'all') return false;

      const dietStyle = userProfile.mealPref_dietStyle;
      const allergies = userProfile.mealPref_allergies || [];
      const religious = userProfile.mealPref_religious || [];

      // Vegetarian/Vegan: hide meat, poultry, organ meats, fish/seafood
      if (dietStyle === 'vegetarian' || dietStyle === 'vegan') {
        if (
          ['meat', 'poultry', 'organ_meat', 'fish_seafood'].includes(
            categoryValue
          )
        ) {
          return true;
        }
      }

      // Vegan: also hide dairy and eggs
      if (dietStyle === 'vegan') {
        if (['dairy', 'eggs'].includes(categoryValue)) {
          return true;
        }
      }

      // Religious restrictions
      if (religious.includes('noAlcohol')) {
        if (categoryValue === 'alcohol') return true;
      }

      // Allergies
      if (allergies.includes('eggFree') && categoryValue === 'eggs') {
        return true;
      }

      if (allergies.includes('dairyFree') && categoryValue === 'dairy') {
        return true;
      }

      if (
        allergies.includes('nutFree') &&
        categoryValue === 'nuts_seeds'
      ) {
        return true;
      }

      if (
        (allergies.includes('fishFree') ||
          allergies.includes('shellfishFree')) &&
        categoryValue === 'fish_seafood'
      ) {
        return true;
      }

      return false;
    },
    [userFoodFilter, userProfile]
  );

  // Filter categories to only show allowed ones
  const allowedCategories = useMemo(
    () => FOOD_CATEGORIES.filter((category) => !isCategoryForbidden(category.value)),
    [isCategoryForbidden]
  );

  const primaryDeficiency = getPrimaryNutrient();

  return (
    <div className="aiu-food-catalog">
      {/* Category Dropdown with Sync Icon */}
      <div className="aiu-food-catalog-dropdown-wrapper">
        <div className="aiu-food-catalog-dropdown">
          <select
            id="food-category-select"
            className="aiu-food-catalog-select"
            value={selectedCategory}
            onChange={handleCategoryChange}
          >
            {allowedCategories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          className="aiu-food-catalog-sync-btn"
          aria-label="Refresh food suggestions"
          title="Refresh food suggestions"
          onClick={handleRefreshClick}
          disabled={selectedCategory === 'all' || loading}
        >
          <FcSynchronize className="aiu-food-catalog-sync-icon" />
        </button>
      </div>

      {/* Recommendations Display */}
      <div className="aiu-collapsible is-open">
        <div className="aiu-card p-3">
          {loading && (
            <div className="aiu-food-catalog-loading">
              <div className="aiu-spinner" />
              <p>Loading recommendations...</p>
            </div>
          )}

          {error && (
            <div className="aiu-food-catalog-error">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && selectedCategory === 'all' && (
            <div className="aiu-food-catalog-empty">
              <p>
                Select a food category to see personalized recommendations
              </p>
            </div>
          )}

          {!loading &&
            !error &&
            selectedCategory !== 'all' &&
            recommendations.length === 0 && (
              <div className="aiu-food-catalog-empty">
                {userFoodFilter?.hasPreferences ? (
                  <>
                    <p>No foods match your current filters in this category.</p>

                    <div className="aiu-food-catalog-empty-details">
                      <small>
                        Your preferences (e.g.{' '}
                        {userFoodFilter.dietStyle !== 'none'
                          ? userFoodFilter.dietStyle
                          : ''}
                        {userProfile?.mealPref_religious?.length
                          ? `, ${userProfile.mealPref_religious.join(', ')}`
                          : ''}
                        ) may be excluding all items here.
                      </small>
                    </div>

                    <div className="aiu-food-catalog-empty-actions">
                      <button
                        className="aiu-food-catalog-action-link"
                        type="button"
                        onClick={() => setSelectedCategory('all')}
                      >
                        Change category
                      </button>
                      <span className="mx-2">|</span>
                      <button
                        className="aiu-food-catalog-action-link"
                        type="button"
                        onClick={goToNutritionSettings}
                      >
                        Edit preferences
                      </button>
                    </div>
                  </>
                ) : (
                  <p>No recommendations found for this category</p>
                )}
              </div>
            )}

          {!loading && !error && recommendations.length > 0 && (
            <div className="aiu-food-catalog-recommendations">
              {primaryDeficiency && (
                <div className="aiu-food-catalog-recommendation-header">
                  <p className="aiu-food-catalog-recommendation-subtitle">
                    Recommended for{' '}
                    <strong>{primaryDeficiency.nutrient}</strong>
                  </p>
                </div>
              )}

              {recommendations.map((food, index) => {
                const topNutrients = getTopNutrients(food, 5);
                const badges = userFoodFilter
                  ? getRelevantBadges(food, userFoodFilter)
                  : [];

                return (
                  <div
                    key={food.id || index}
                    className="aiu-food-catalog-recommendation-item"
                  >
                    <div className="aiu-food-catalog-recommendation-main">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <div className="aiu-food-catalog-recommendation-name">
                          {index + 1}. {food.name}
                        </div>

                        {/* Render Badges */}
                        <div className="aiu-food-item-badges">
                          {badges.map((badge, badgeIndex) => (
                            <span
                              key={badgeIndex}
                              className={`aiu-food-item-badge aiu-food-item-badge--${badge.type}`}
                            >
                              {badge.label}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="aiu-food-catalog-recommendation-nutrients">
                        {primaryDeficiency && (
                          <span className="aiu-food-catalog-nutrient-highlight">
                            {primaryDeficiency.nutrient}:{' '}
                            {formatNutrientValue(
                              food.nutrientsPer100g?.[
                                NUTRIENT_FIELD_MAP[
                                  primaryDeficiency.nutrient
                                ]
                              ],
                              primaryDeficiency.unit || 'mg'
                            )}{' '}
                            / 100g
                          </span>
                        )}

                        {topNutrients.map((nutrient, nutrientIndex) => (
                          <span
                            key={nutrientIndex}
                            className="aiu-food-catalog-nutrient-secondary"
                          >
                            {nutrient.name}:{' '}
                            {formatNutrientValue(
                              nutrient.value,
                              nutrient.unit
                            )}{' '}
                            / 100g
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AiFoodCatalog;
export { FOOD_CATEGORIES, DIETARY_FILTERS, goToNutritionSettings };
